import { defineComponent as _defineComponent } from 'vue'
import CountriesRepository from '@/js/common/Domain/Repository/Countries';
import abTests from '@/js/common/Domain/Service/abTests';
import {
    SiteHeader,
    SearchForm,
    DepartureCity,
    Selection,
    NightsRange,
    DateFrom,
    TouristsSelection,
    type Country,
    type Resort,
    type TouristRoom,
    useStickyOnScroll,
    DestinationPrices,
    type SearchFormCriteria,
 type SearchFormExpose } from '@travelata-libs/travelata-frontend';
 import { format } from 'date-fns';
import {
    ref, computed,
} from 'vue';
import { useState } from 'vuex-composition-helpers';
import { type IMapForSearchFormState, } from '@ve/pages/serp/store';
import { NAMESPACE as MAP_FOR_SEARCH_FORM,  } from '@ve/pages/serp/store/SearchFormStore';

interface LibSearchFormHostProps {
    disabledDestination?: boolean;
    authData: { user: { email: null | string, name: null | string }, accountUrl: string }
}
interface LibSearchFormHostEmits {
    (event: 'city', data: IMapForSearchFormState['departureCity']): void;
    (event: 'checkInDate', data: IMapForSearchFormState['checkInDateRange']): void;
    (event: 'tourists', data: IMapForSearchFormState['touristGroup']): void;
    (event: 'resorts', data: IMapForSearchFormState['resorts']): void;
    (event: 'countries', data: IMapForSearchFormState['countries']): void;
    (event: 'nights', data: IMapForSearchFormState['nightRange']): void;
    (event: 'hotels', data: IMapForSearchFormState['hotels']): void;
    (event: 'logout'): void;
    (event: 'login'): void;
    (event: 'search'): void;
    (event: 'buy'): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'LibSearchFormHost',
  props: {
    disabledDestination: { type: Boolean },
    authData: null
  },
  emits: ["city", "checkInDate", "tourists", "resorts", "countries", "nights", "hotels", "logout", "login", "search", "buy"],
  setup(__props: any, { expose, emit }: { emit: ({
    (event: 'city', data: IMapForSearchFormState['departureCity']): void;
    (event: 'checkInDate', data: IMapForSearchFormState['checkInDateRange']): void;
    (event: 'tourists', data: IMapForSearchFormState['touristGroup']): void;
    (event: 'resorts', data: IMapForSearchFormState['resorts']): void;
    (event: 'countries', data: IMapForSearchFormState['countries']): void;
    (event: 'nights', data: IMapForSearchFormState['nightRange']): void;
    (event: 'hotels', data: IMapForSearchFormState['hotels']): void;
    (event: 'logout'): void;
    (event: 'login'): void;
    (event: 'search'): void;
    (event: 'buy'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    disabledDestination?: boolean;
    authData: { user: { email: null | string, name: null | string }, accountUrl: string }
}



const searchFormRef = ref<SearchFormExpose & { $el: HTMLElement } | null>(null);

const {
    checkInDateRange, 
    countries,
    departureCity,
    nightRange,
    resorts,
    touristGroup,
    hotels,
} = useState<IMapForSearchFormState>(MAP_FOR_SEARCH_FORM, ['checkInDateRange', 'countries', 'departureCity', 'nightRange', 'resorts', 'touristGroup', 'hotels'])

const headerEl = ref<HTMLElement | null>(null);
const destinationPrices = ref<DestinationPrices>({ country: {}, resort: {} });
const { collapsed, progress } = useStickyOnScroll({ target: headerEl });

CountriesRepository.repository.on('minPricesLoaded', function({ countries, resorts }: { countries: Backbone.Model<Country>[], resorts: Backbone.Model<Resort>[] }) {
    countries.forEach((model: Backbone.Model<Country>) => {
        destinationPrices.value.country![model.get('id')] = model.get('minPrice') || 0;
    });
    resorts.forEach((model: Backbone.Model<Resort>) => {
        destinationPrices.value.resort![model.get('id')] = model.get('minPrice') || 0;
    });
    destinationPrices.value = { ...destinationPrices.value };
});


const isCollapsed = computed(() => collapsed.value);
const headerOpacity = computed(() => 1 - progress.value);

const libNightRange = computed<SearchFormCriteria['nights']>(() => ({
    from: nightRange.value?.from || 0,
    to: nightRange.value?.to || 0,
}));

const libCheckInDateRange = computed<SearchFormCriteria['dateFrom']>(() => {
    return ({
        from: format(checkInDateRange.value?.from, 'yyyy-MM-dd'),
        to: format(checkInDateRange.value?.to, 'yyyy-MM-dd'),
        daysFlexible: checkInDateRange.value?.daysFlexible || 0,
    })
});

const libDepartureCity = computed<SearchFormCriteria['fromCity']>(() => departureCity.value);

const libTouristsGroup = computed<SearchFormCriteria['tourists']>(() => {
    const all = [touristGroup.value, ...touristGroup.value?.touristsByRooms || []];
    return { rooms: all.map((t) => ({ adults: t?.adults || 0, childrenAges: (t?.ages || []) }))};
    
});

const adaptResorts = (resorts: IMapForSearchFormState['resorts'], country: Country): Resort[] => {
    return resorts?.map((r) => ({
        id: r.id,
        name: r.name,
        isPopular: r.isPopular,
        minPrice: r.minPrice,
        countryId: country.id,
        countryName: country.name,
    }))
};

const libDestination = computed<SearchFormCriteria['destination'] | null>(() => {
    const country: Country & { resorts: IMapForSearchFormState['resorts'] } = countries.value[0];
    if(!country) return null;
    if (hotels.value.length > 0) {
        return {
            kind: 'hotels',
            hotels: hotels.value.map((h) => ({ id: h.id, name: h.name, countryId: country.id, countryName: country.name })),
            country,
        }
    }
    const libResorts: Resort[] = adaptResorts(resorts.value, country);
    if(libResorts.length > 0) {
        return {
            kind: 'resorts',
            country,
            resorts: libResorts,
        }
    };
    return {
        kind: 'country',
        country,
        resorts: adaptResorts(country.resorts, country),
    }
})

function onUpdateFromCity(city: DepartureCity | null): void {
 if (!city) return;                       // отсекаем null
    emit('city', {
        id: city.id,
        name: city.name,
        countriesWithDirectFlights: city.countriesWithDirectFlights ?? [],
    });
}
function onUpdateDestination(selection: Selection): void {
    if(selection.kind === 'resorts') {
        emit('resorts', selection.resorts as any);
    };
    if(selection.kind === 'country') {
        emit('countries', [{ ...selection.country, resorts: selection.resorts }])
    }
    if(selection.kind === 'hotels') {
        emit('hotels', selection.hotels.map((h) => ({ id: h.id, name: h.name, country: h.countryId })))
    }
}
function onUpdateNights(range: NightsRange): void {
    emit('nights', {
        from: range.from,
        to: range.to,
    })
}
function onUpdateDateFrom(value: DateFrom): void {
    emit('checkInDate', {
        from: new Date(value.from),
        to: new Date(value.to),
        daysFlexible: value.daysFlexible,
    })
}
const adaptTouristsGroup = (t: TouristRoom) => ({
    adults: t.adults,
    kids: t.childrenAges.filter((age) => age > 1).length,
    infants:  t.childrenAges.filter((age) => age <= 1).length,
    ages: t.childrenAges,
    touristsByRooms: [],
})
function onUpdateTourists(tourists: TouristsSelection): void {
    const [first, ...other] = tourists.rooms;
    if(!first) return;
    emit('tourists', {
      ...adaptTouristsGroup(first),
      touristsByRooms: other.map(adaptTouristsGroup),
    })
}

function onSearch(): void {
    emit('search');
}
function onBuy(): void {
    emit('buy');
}
expose({
    validate: (): boolean => (searchFormRef.value ? searchFormRef.value.validate() : true),
});

return { __sfc: true,props, emit, searchFormRef, checkInDateRange, countries, departureCity, nightRange, resorts, touristGroup, hotels, headerEl, destinationPrices, collapsed, progress, isCollapsed, headerOpacity, libNightRange, libCheckInDateRange, libDepartureCity, libTouristsGroup, adaptResorts, libDestination, onUpdateFromCity, onUpdateDestination, onUpdateNights, onUpdateDateFrom, adaptTouristsGroup, onUpdateTourists, onSearch, onBuy, abTests, SiteHeader, SearchForm }
}

})